import { useRef } from "preact/hooks";

import * as panes from "../lib/panes";

/**
 * The edge between two columns, as something a person can move.
 *
 * A `separator` with a `tabindex` is a real control rather than a decorated
 * border: it takes focus, it reports where it is and how far it may go, and the
 * arrow keys move it. That half of the feature is not a courtesy -- a divider
 * that only answers a drag is unusable to anyone who does not drag, and the
 * width of the message list is exactly the kind of thing someone who works by
 * keyboard has an opinion about.
 *
 * Pointer events rather than mouse events, so a trackpad, a pen and a finger
 * are the same gesture and there is one path to test rather than three.
 *
 * It draws nothing at rest: the columns already have a border between them, and
 * a splitter that stripes the page is the wrong trade for a control most people
 * touch twice a year. The strip is wider than the line it sits over, which is
 * what makes it possible to hit.
 */

/** How far an arrow key moves an edge, and how far Shift makes it move. */
const STEP = 16;
const LEAP = 64;

/** What each edge resizes, said as the thing rather than as the gesture. */
const NAMES: Record<panes.Edge, string> = {
  sidebar: "Mailboxes column width",
  list: "Message list column width",
};

export function PaneDivider({ edge }: { edge: panes.Edge }) {
  const drag = useRef<{ id: number; x: number; from: number } | null>(null);
  const width = panes.widths.value[edge];
  const limit = panes.bounds.value[edge];

  // Idempotent, because more than one of the three endings can fire for the same
  // gesture: a pointer released over the page sends `pointerup` and then
  // `lostpointercapture`, and a browser that revokes the capture by itself sends
  // only the second.
  const finish = () => {
    if (!drag.current) return;
    drag.current = null;
    panes.endDrag();
  };

  const onKey = (e: KeyboardEvent) => {
    const step = e.shiftKey ? LEAP : STEP;
    switch (e.key) {
      case "ArrowLeft":
        panes.keyTo(edge, width - step);
        break;
      case "ArrowRight":
        panes.keyTo(edge, width + step);
        break;
      case "Home":
        panes.keyTo(edge, limit.min);
        break;
      case "End":
        panes.keyTo(edge, limit.max);
        break;
      case "Backspace":
      case "Delete":
        // The keyboard's half of the double-click below. A way back to the
        // default that only a mouse can reach is a preference a keyboard user
        // cannot undo without clearing site data.
        panes.reset(edge);
        break;
      default:
        return;
    }
    // Only for the keys above, and only once one of them has been handled:
    // Home and End would otherwise scroll the page, and every other key --
    // Tab out, or the app's own single-letter shortcuts -- still means what it
    // means everywhere else.
    e.preventDefault();
  };

  return (
    <div
      class="pane-divider"
      data-edge={edge}
      role="separator"
      aria-orientation="vertical"
      aria-label={NAMES[edge]}
      aria-valuenow={width}
      aria-valuemin={limit.min}
      aria-valuemax={limit.max}
      // The number on its own is announced as a bare integer, which could be a
      // percentage, a column count or a position in a list.
      aria-valuetext={`${width} pixels`}
      title="Drag, or use the arrow keys. Double-click to reset."
      tabIndex={0}
      onPointerDown={(e) => {
        // The primary button only. A right-click on a splitter is not a drag,
        // and capturing it would swallow the context menu with it.
        if (e.button !== 0) return;
        // Capture, so the rest of the gesture belongs to this element however
        // far it travels -- over the sandboxed iframe the message body is drawn
        // in, past the edge of the window, or off the screen entirely. It is
        // also what guarantees an ending: a pointer released outside the window
        // still delivers `pointerup` here, and a browser that takes the capture
        // back sends `lostpointercapture` instead.
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { id: e.pointerId, x: e.clientX, from: width };
        panes.dragTo(edge, width);
        // Stops the press selecting the text in the columns either side of it,
        // which also costs the divider the focus the browser would have given
        // it -- so it is taken back by hand, and the arrow keys carry on from
        // where the drag stopped.
        e.preventDefault();
        e.currentTarget.focus();
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        // A second finger on a touchscreen is a different pointer, and this one
        // is not the gesture that started here.
        if (!d || d.id !== e.pointerId) return;
        panes.dragTo(edge, d.from + (e.clientX - d.x));
      }}
      onPointerUp={finish}
      onPointerCancel={finish}
      onLostPointerCapture={finish}
      onDblClick={() => panes.reset(edge)}
      onKeyDown={onKey}
    />
  );
}
